import React, { useCallback, useEffect, useState, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { FormControl, Grid, InputLabel, MenuItem, Paper, Select, TextField, IconButton, Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { fetchCleanClaimsChart, MenuProps } from '../../Widgets/WidgetService';
import { configureStackedSplitBarChart } from '../../ChartConfiguration/ChartConfiguration';
import EncounterSummary from '../../../Models/Widget/EncounterSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import Authorization from '../../../utils/Authorization';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';
import '../Widgets.css';

const CleanClaimSummary = (props) => {
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [selectedProvider, setSelectedProvider] = useState<string>('All');
    const [selectedPlans, setSelectedPlans] = useState<string[]>(['All']);
    const [encounterOptions, setEncounterOptions] = useState({});
    const [isEncounterGraphShow, setIsEncounterGraphShow] = useState<boolean>(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState<number>(2);
    const [encounterSummary, setEncounterSummary] = useState<EncounterSummary>();
    const [selectedTeam, setSelectedTeam] = useState('All');
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const [showInActiveTeams, setShowInActiveTeams] = useState(false);
    const [showInActiveProviders, setShowInActiveProviders] = useState<boolean>(false);
    const filteredProviders = (showInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const chartHeight = 440;

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (event) => {
        setShow(!show);
    };

    const dateFilterList = [
        { Id: 1, Value: 'Last 3 Months' },
        { Id: 2, Value: 'Last 6 Months' },
        { Id: 3, Value: 'Last 1 Year' },
        { Id: 4, Value: 'Custom' },
    ];

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isEncounterGraphShow) {
            setEncounterOptions(
                configureStackedSplitBarChart(
                    'Clean Claim Summary',
                    'Claims Count',
                    'Month',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    '',
                    '',
                    false,
                    chartHeight
                )
            );
        }

        setSelectedTeam(authData.getDefaultTeamId(''));
        setIsFetched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetched, setIsFetched, isEncounterGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (selectedFilter !== TimePeriodFilterEnum.Custom || (Boolean(fromDate) && Boolean(toDate))) {
            const changeFilter = async () => {
                let start = fromDate !== null ? new Date(fromDate).toDateString() : null;
                let end = toDate !== null ? new Date(toDate).toDateString() : null;

                const data = await fetchCleanClaimsChart(
                    selectedProvider,
                    selectedTeam,
                    selectedPlans.join(","),
                    selectedFilter,
                    start,
                    end,
                    showInActiveTeams,
                    showInActiveProviders
                );
                setEncounterSummary(data);
            };

            setIsEncounterGraphShow(false);
            setEncounterOptions(
                configureStackedSplitBarChart(
                    'Clean Claim Summary',
                    'Claims Count',
                    'Month',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    '',
                    '',
                    false,
                    chartHeight
                )
            );
            changeFilter();
        }

        if (selectedFilter !== TimePeriodFilterEnum.Custom && Boolean(fromDate) && Boolean(toDate)) {
            setFromDate(null);
            setToDate(null);
        }
    }, [selectedProvider, selectedPlans, selectedFilter, fromDate, toDate, selectedTeam, showInActiveTeams, showInActiveProviders]);

    useEffect(() => {
        if (encounterSummary) {
            let xAxisValues = [];
            let yAxisValues = [];
            let totClaimsValues = [];
            let cleanClaimsValues = [];
            for (var data in encounterSummary.Encounters) {
                let dataInfo = encounterSummary.Encounters[data];
                let percentage = dataInfo.EncounterCount > 0 ? (dataInfo.CleanClaimCount / dataInfo.EncounterCount) * 100 : 0;

                let d = {
                    y: dataInfo.CleanClaimCount,
                    name: Math.round(percentage),
                };
                cleanClaimsValues.push(d);

                let d1 = {
                    y: dataInfo.EncounterCount,
                    name: '',
                };
                totClaimsValues.push(d1);

                xAxisValues.push(dataInfo.Date);
            }
            yAxisValues.push({ name: 'Total Claims', data: totClaimsValues, color: '#4682B4', showInLegend: true });
            yAxisValues.push({ name: 'Clean Claims', data: cleanClaimsValues, color: '#00a652', showInLegend: true });

            if (encounterSummary.Encounters.length === 0) {
                setEncounterOptions(
                    configureStackedSplitBarChart(
                        'Clean Claim Summary',
                        'Claims Count',
                        'Month',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [0],
                        '',
                        '',
                        false,
                        chartHeight
                    )
                );
            } else {
                let subTitle = '',
                    xAxisTitle = '',
                    yAxisTitle = 'Count';
                setEncounterOptions(
                    configureStackedSplitBarChart('Encounter Chart', subTitle, xAxisTitle, yAxisTitle, yAxisValues, xAxisValues, '', '', false, chartHeight)
                );
            }
            setIsEncounterGraphShow(true);
        }
    }, [encounterSummary]);

    const changeFromDate = (date: any) => {
        setFromDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(toDate) || new Date(toDate) <= dateInfo) {
            let endDate = dateInfo.setDate(dateInfo.getDate() + 30);
            setToDate(endDate);
        }
    };

    const changeToDate = (date: any) => {
        setToDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(fromDate) || new Date(fromDate) >= dateInfo) {
            let startDate = dateInfo.setDate(dateInfo.getDate() - 30);
            setFromDate(startDate);
        }
    };

    const toggleInactiveHandler = (isChecked: boolean) => {
        setShowInActiveProviders(isChecked);
        setSelectedProvider('All');
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} textAlign="center" ref={ref}>
                            <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'}>
                                Clean Claim Summary
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="body2">Total submitted claims vs clean claims</Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Clean Claim :
                                            </Typography>
                                            <Typography variant="caption">Claims which is accepted and acknowledged by payer</Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        {props.hasElevatedFilterAccess && (
                            <Grid size={4}>
                                <TeamDropdown
                                    teams={teams}
                                    selectedTeam={selectedTeam}
                                    onChange={(id) => setSelectedTeam(id)}
                                    showInactive={showInActiveTeams}
                                    onToggleInactive={setShowInActiveTeams}
                                />
                            </Grid>)}
                        <Grid size={4}>
                            <HealthPlanMultiSelect
                                value={selectedPlans}
                                onChange={setSelectedPlans}
                                menuProps={MenuProps}
                            />
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Providers</InputLabel>
                                <Select
                                    value={selectedProvider}
                                    label="Providers"
                                    name="Providers"
                                    onChange={(e) => setSelectedProvider(e.target.value)}
                                    MenuProps={MenuProps}
                                >
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={<Checkbox checked={showInActiveProviders} onChange={(e) => toggleInactiveHandler(e.target.checked)} />}
                                        label="Show Inactive"
                                    />
                                    {filteredProviders.map((pro) => (
                                        <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                            {pro.LastName}, {pro.FirstName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={selectedFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setSelectedFilter(parseInt(e.target.value as any))}
                                >
                                    {dateFilterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {selectedFilter === TimePeriodFilterEnum.Custom ? (
                            <React.Fragment>
                                <Grid size={{ md: 4 }}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={Boolean(fromDate) ? fromDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeFromDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={Boolean(toDate) ? toDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeToDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : null}
                        <Grid size={12} marginTop={1} sx={{ position: 'relative' }}>
                            {!isEncounterGraphShow ? (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            ) : null}
                            <HighchartsReact highcharts={Highcharts} options={encounterOptions} />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default CleanClaimSummary;
